/**
 * Column header renderer for the Data Explorer grid.
 *
 * Displays column label, type badge, statistics (missing %, distinct count), and sparkline.
 */

import React from 'react';
import { useTheme, fontSize, fontWeight, Icon } from '@fti/design-system';
import { TYPE_BADGE } from './format';
import type { ColumnHeaderProps } from './contracts';
import { Sparkline } from './charts/Sparkline';

export function ColumnHeader(props: ColumnHeaderProps) {
  const { column, summary, sortDirection, filtered, selected, onSort, onOpenFilter, onSelect, onExpandGroup } = props;
  const { theme } = useTheme();

  const containerStyle: React.CSSProperties = {
    height: '72px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 6px',
    background: selected ? theme.colors.primary[50] : theme.colors.background.paper,
    borderBottom: `1px solid ${theme.colors.border.default}`,
  };

  const buttonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    color: theme.colors.text.secondary,
  };

  const buttonFocusStyle: React.CSSProperties = {
    outline: `2px solid ${theme.colors.border.focus}`,
    outlineOffset: '2px',
  };

  // Row 1: Label and action buttons
  const row1Style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minHeight: '20px',
  };

  const labelButtonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: theme.colors.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    textAlign: 'left',
  };

  // Row 2: Type badge and stats
  const row2Style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '18px',
    marginTop: '2px',
  };

  const badgeStyle: React.CSSProperties = {
    fontSize: '10px',
    padding: '1px 4px',
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: '3px',
    color: theme.colors.text.secondary,
  };

  const statStyle: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: theme.colors.text.secondary,
  };

  // Row 3: Sparkline
  const row3Style: React.CSSProperties = {
    marginTop: 'auto',
    height: '22px',
    display: 'flex',
    alignItems: 'center',
  };

  // Determine type badge text
  const badgeText = column.kind === 'group'
    ? TYPE_BADGE.object
    : TYPE_BADGE[summary?.type ?? column.column.type] ?? TYPE_BADGE.mixed;

  // Format missing percentage
  const formatMissingPct = (pct: number): React.ReactNode => {
    if (pct === 0) {
      return (
        <span style={{ ...statStyle, opacity: 0.5 }}>
          0% ∅
        </span>
      );
    }
    const color = pct >= 50 ? theme.colors.semantic.warning.dark : theme.colors.text.secondary;
    return (
      <span style={{ ...statStyle, color }}>
        {pct}% ∅
      </span>
    );
  };

  // Sort icon
  const getSortIcon = (): string => {
    if (sortDirection === 'asc') return 'arrow-up';
    if (sortDirection === 'desc') return 'arrow-down';
    return 'chevron-down';
  };

  const sortIconColor = sortDirection ? theme.colors.text.primary : theme.colors.text.secondary;
  const sortIconOpacity = sortDirection ? 1 : 0.4;

  // Filter icon color
  const filterIconColor = filtered ? theme.colors.primary[600] : theme.colors.text.secondary;

  return (
    <div style={containerStyle}>
      {/* Row 1: Label and buttons */}
      <div style={row1Style}>
        <button
          style={labelButtonStyle}
          onClick={onSelect}
          title={column.id}
          onFocus={(e) => Object.assign(e.currentTarget.style, buttonFocusStyle)}
          onBlur={(e) => (e.currentTarget.style.outline = 'none')}
        >
          {column.label}
        </button>

        {column.kind === 'leaf' && (
          <>
            <button
              style={{ ...buttonStyle, color: sortIconColor, opacity: sortIconOpacity }}
              onClick={onSort}
              aria-label={`Sort by ${column.label}`}
              onFocus={(e) => Object.assign(e.currentTarget.style, buttonFocusStyle)}
              onBlur={(e) => (e.currentTarget.style.outline = 'none')}
            >
              <Icon name={getSortIcon()} size={14} />
            </button>

            <button
              style={{ ...buttonStyle, color: filterIconColor }}
              onClick={(e) => onOpenFilter(e.currentTarget)}
              aria-label={`Filter ${column.label}`}
              onFocus={(e) => Object.assign(e.currentTarget.style, buttonFocusStyle)}
              onBlur={(e) => (e.currentTarget.style.outline = 'none')}
            >
              <Icon name="filter" size={14} />
            </button>
          </>
        )}

        {column.kind === 'group' && onExpandGroup && (
          <button
            style={buttonStyle}
            onClick={onExpandGroup}
            aria-label={`Expand ${column.label}`}
            onFocus={(e) => Object.assign(e.currentTarget.style, buttonFocusStyle)}
            onBlur={(e) => (e.currentTarget.style.outline = 'none')}
          >
            <Icon name="plus" size={14} />
          </button>
        )}
      </div>

      {/* Row 2: Type badge and stats */}
      <div style={row2Style}>
        <span style={badgeStyle}>{badgeText}</span>

        {summary && (
          <>
            {formatMissingPct(Math.round(summary.missingPct))}
            <span style={statStyle}>· {summary.distinct} uniq</span>
          </>
        )}
      </div>

      {/* Row 3: Sparkline */}
      {summary?.spark && (
        <div style={row3Style}>
          <Sparkline spark={summary.spark} width={column.width - 12} height={20} />
        </div>
      )}
    </div>
  );
}
